import { BIRD_PORTRAIT } from '@dove/shared';
import { birdPortraitUrl, seedBirdPortrait } from './api';

/**
 * Where a bird's portrait comes from, in the order worth trying
 * (docs/5 § D260806-1118).
 *
 * ONE ANSWER PER APPEARANCE, EVER, ON THIS DEVICE. A feed of twenty letters is
 * commonly six distinct birds, and every surface that draws a person asks this
 * module — so the whole design is about making the twenty-first ask free. Four
 * layers, each strictly cheaper than the one under it:
 *
 *   1. **This session's map.** Synchronous after the first resolve, so scrolling a
 *      feed of already-seen birds costs a property lookup. It also coalesces: two
 *      rows asking for one bird at the same moment share one promise, which is
 *      what stops a feed mounting six identical renders in six frames.
 *   2. **The Cache Storage entry**, keyed by the portrait's own URL. Survives a
 *      reload and a cold start, and it holds BOTH origins of the bytes — one
 *      somebody else captured and one this device captured — because the whole
 *      point is that a portrait is a portrait whoever took it. Without it every
 *      launch re-renders every bird on screen, which is about 15 ms each on the
 *      main thread and lands all at once.
 *   3. **The shared copy**, over the network. Somebody else's device already made
 *      this exact picture and the server kept it (see `routes/birds.ts`), so the
 *      cheapest correct thing is to download ten kilobytes rather than parse a
 *      350 KB GLB to produce the same pixels.
 *   4. **This device's GPU.** Always available, always correct, and the only layer
 *      that can answer for a bird nobody has ever worn before.
 *
 * AND IT NEVER RETURNS NOTHING. A device with no WebGL, a capture that throws, a
 * code from a build ahead of this one — all three resolve to `null`, and every
 * caller draws the 2D bird instead. The portrait is an upgrade to the byline, not
 * a precondition for it.
 */

/** What is known about one appearance. */
type Entry =
  /** Resolved: an `<img src>`, or `null` if this device cannot draw it. */
  | { state: 'done'; src: string | null }
  /** In flight. Every later asker awaits the same promise. */
  | { state: 'busy'; wait: Promise<string | null> };

const entries = new Map<string, Entry>();

/** The Cache Storage bucket. Named with the epoch so a renderer change orphans
 *  the previous generation rather than serving it — see `BIRD_PORTRAIT.EPOCH`. */
const CACHE_NAME = `dove-bird-portraits-b${BIRD_PORTRAIT.EPOCH}`;

/**
 * Whether this device has somewhere to keep a capture.
 *
 * Cache Storage needs a secure context, which is every channel this ships on
 * (https on the web, `https://localhost` inside both Capacitor shells) — but a
 * plain-http dev server on a LAN address is not one, and the portrait must still
 * work there. So it is asked rather than assumed, and a device without it simply
 * re-renders on each launch.
 */
function store(): Promise<Cache> | null {
  if (typeof caches === 'undefined') return null;
  try {
    return caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

/* --------------------------------------------------------------------------
 * The render queue
 * ----------------------------------------------------------------------- */

/**
 * Captures happen ONE AT A TIME, AND AT MOST ONE PER FRAME.
 *
 * Two separate reasons, both load-bearing. One at a time because
 * `three/portrait.ts` is a single renderer with one model mounted in it, so
 * concurrent captures would recolour each other's bird. One per frame because a
 * capture is main-thread WebGL work: six of them back to back is a visible stall
 * on the frame a feed mounts, which is exactly the frame a person is looking at.
 * Spread across six frames it is invisible, and nothing on screen is waiting — the
 * 2D bird is already drawn in the right colours.
 */
const queue: (() => Promise<void>)[] = [];
let draining = false;

function drain(): void {
  if (draining) return;
  draining = true;
  const step = () => {
    const next = queue.shift();
    if (!next) {
      draining = false;
      return;
    }
    void next().finally(() => {
      /* `requestAnimationFrame` rather than a timer, so captures stop entirely
         while the tab is in the background — a phone should not be spinning up a
         GPU context for a feed nobody is looking at, and the work is not urgent by
         construction. It resumes on the next paint. */
      requestAnimationFrame(step);
    });
  };
  requestAnimationFrame(step);
}

function enqueue<T>(job: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    queue.push(() => job().then(resolve, reject).then(() => undefined));
    drain();
  });
}

/* --------------------------------------------------------------------------
 * The lookup
 * ----------------------------------------------------------------------- */

/**
 * The portrait for one appearance, or `null` if this device cannot draw it.
 *
 * THE TWO FLAGS ARE ABOUT THE SERVER, AND NEITHER IS AN OPTIMISATION.
 *
 * `preview` says *do not ask*. The closet draws appearances nobody has saved —
 * every plumage swatch and every pose tile is a bird that may exist only for as
 * long as somebody's finger is on it — so asking the server would be sixteen
 * guaranteed 404s on opening a drawer.
 *
 * `mine` says *do offer*. Only the appearance an account has actually committed to
 * may be uploaded, because `routes/birds.ts` will refuse anything else and because
 * a bucket full of postures nobody kept is a bucket full of nothing. It is set for
 * the viewer's own committed bird and nowhere else: somebody else's portrait is
 * theirs to seed, from their own device.
 */
export function birdPortrait(
  code: string,
  options: { mine?: boolean; preview?: boolean } = {},
): Promise<string | null> {
  const found = entries.get(code);
  if (found) {
    /*
     * ALREADY RESOLVED, BUT PERHAPS NOT YET OFFERED — and this is the gap that
     * closes. The closet resolves every tile as a `preview`, deliberately never
     * uploading any of them; one of those tiles is the appearance the viewer
     * already wears. So opening the drawer before the feed had drawn a byline used
     * to mean this device never seeded its own bird for the rest of the session,
     * silently. The picture is already in hand, so this needs no second render.
     */
    if (options.mine && found.state === 'done' && found.src) offer(code, found.src);
    return found.state === 'done' ? Promise.resolve(found.src) : found.wait;
  }

  const wait = resolve(code, options).then(
    (src) => {
      entries.set(code, { state: 'done', src });
      return src;
    },
    () => {
      /* A failure is cached as "no portrait" rather than retried on every render.
         A code that cannot be drawn on this device cannot be drawn on the next
         scroll either, and retrying would mean a broken capture costs a GPU
         attempt per row per frame. */
      entries.set(code, { state: 'done', src: null });
      return null;
    },
  );
  entries.set(code, { state: 'busy', wait });
  return wait;
}

/** Synchronously, only if this session already knows. Lets a component paint the
 *  portrait on its FIRST render rather than swapping into it a frame later. */
export function birdPortraitNow(code: string): string | null | undefined {
  const found = entries.get(code);
  return found?.state === 'done' ? found.src : undefined;
}

/**
 * Whether these bytes are a bird rather than an empty frame
 * (docs/5 § D260808-0218).
 *
 * A PORTRAIT CAN FAIL BY SUCCEEDING. A capture taken while the GPU has reclaimed
 * the drawing context comes back as a real, well-formed, correctly-sized PNG with
 * nothing in it — `three/portrait.ts` now catches that where it happens and says
 * why, and this is the net under every OTHER way an empty one could arrive: out of
 * a cache written by a build that had the bug, or off a server somebody else's
 * device seeded before it was fixed. Both of those outlive the fix, and neither is
 * something a person can do anything about, so they are checked rather than
 * assumed away. See `BIRD_PORTRAIT.MIN_BYTES` for why a byte count is the test and
 * where the number comes from.
 */
const drawn = (blob: Blob): boolean => blob.size >= BIRD_PORTRAIT.MIN_BYTES;

async function resolve(
  code: string,
  options: { mine?: boolean; preview?: boolean },
): Promise<string | null> {
  const url = birdPortraitUrl(code);
  const cache = await store();

  /* Layer 2. Asked even for a preview: a pose tried and abandoned last week is
     still the same picture, and a cache hit is cheaper than a render. */
  const held = await cache?.match(url).catch(() => undefined);
  if (held?.ok) {
    const blob = await held.blob();
    if (drawn(blob)) return URL.createObjectURL(blob);
    /* An empty one, kept by a build that could produce them. Dropped rather than
       stepped over, so the device heals on the next look instead of re-deciding
       this every launch for as long as the entry survives. */
    await cache?.delete(url).catch(() => {});
  }

  /* Layer 3. Skipped for a preview — see `mine` above. */
  if (!options.preview) {
    const shared = await fetch(url).catch(() => null);
    if (shared?.ok) {
      const blob = await shared.blob();
      /* An empty one from the bucket is not cached and not shown; this device can
         draw the bird itself, which is what the layer below is for. */
      if (drawn(blob)) {
        /* Kept by us as well as by the HTTP cache. The response is `immutable`, so
           the browser will usually have it — but "usually" is doing a lot of work on
           a phone under storage pressure, and this is the copy that makes a cold
           start free. */
        await cache?.put(url, new Response(blob, {
          headers: { 'content-type': 'image/png' },
        })).catch(() => {});
        return URL.createObjectURL(blob);
      }
    }
  }

  /* Layer 4. */
  const { canCapture, capturePortraitCode } = await import('@/three/portrait');
  if (!canCapture()) return null;
  const png = await enqueue(() => capturePortraitCode(code));
  if (!png) return null;
  /* Neither kept nor offered. The 2D bird is the answer for this appearance on
     this device, which is the same answer a device with no WebGL gets. */
  if (!drawn(png)) return null;

  await cache?.put(url, new Response(png, {
    headers: { 'content-type': 'image/png' },
  })).catch(() => {});

  const src = URL.createObjectURL(png);
  if (options.mine) offer(code, src);
  return src;
}

/** Codes this device has already offered. One attempt per appearance per session,
 *  whatever asks: a refusal is expected traffic and retrying it is pointless. */
const offered = new Set<string>();

/**
 * Offer a capture to everybody else, once.
 *
 * FIRE AND FORGET, ON PURPOSE. The picture is already on screen, and the server
 * refuses for four ordinary reasons — somebody seeded it first, the daily ceiling,
 * a stale session, no network — not one of which is anything a person should be
 * told about. Reading the bytes back out of the object URL rather than threading a
 * `Blob` through every caller: the browser holds them either way, and it means the
 * already-resolved path above can offer without re-rendering.
 */
function offer(code: string, src: string): void {
  if (offered.has(code)) return;
  offered.add(code);
  void fetch(src)
    .then((r) => r.blob())
    .then((png) => seedBirdPortrait(code, png))
    .catch(() => {});
}

/**
 * Forget one appearance, so the next ask re-resolves it.
 *
 * FOR THE CLOSET, and specifically for the moment somebody KEEPS a bird they were
 * previewing. That code was resolved with `preview` set, so it was never offered
 * to the server — and it is now the account's committed appearance, which is
 * exactly the thing that should be shared. Dropping the entry lets the next render
 * take the ordinary path and seed it.
 */
export function forgetBirdPortrait(code: string): void {
  const found = entries.get(code);
  if (found?.state === 'done' && found.src) URL.revokeObjectURL(found.src);
  entries.delete(code);
}
